import React, { useMemo } from 'react';
import { View, Text } from 'react-native';
import type { ResponseCount } from '../lib/api';

type Props = {
  counts: ResponseCount[];
  options?: { label: string; emoji: string }[];
  barColor?: string;
  height?: number;
};

export function ResponseCountsChart({ counts, options, barColor = '#4F46E5', height = 220 }: Props) {
  const data = useMemo(() => {
    const byIndex = new Map<number, number>();
    for (const c of counts) {
      const v = byIndex.get(c.option_index) || 0;
      byIndex.set(c.option_index, v + c.count);
    }
    // ensure zero bars exist for missing option indices when options are provided
    if (Array.isArray(options)) {
      for (let i = 0; i < options.length; i++) {
        if (!byIndex.has(i)) byIndex.set(i, 0);
      }
    }
    const entries = Array.from(byIndex.entries()).sort((a, b) => a[0] - b[0]);
    const maxVal = Math.max(1, ...entries.map(([, v]) => v));
    return { entries, maxVal };
  }, [counts]);

  return (
    <View style={{ width: '100%', paddingVertical: 8 }}>
      <View style={{ flexDirection: 'row', height }}>
        {/* Y-axis */}
        <View style={{ width: 36, alignItems: 'flex-end', justifyContent: 'space-between', paddingVertical: 8 }}>
          <Text style={{ fontSize: 11, color: '#666' }}>{data.maxVal}</Text>
          <Text style={{ fontSize: 11, color: '#666' }}>{Math.ceil(data.maxVal / 2)}</Text>
          <Text style={{ fontSize: 11, color: '#666' }}>0</Text>
        </View>
        {/* Bars */}
        <View style={{ flex: 1, flexDirection: 'row', alignItems: 'flex-end', paddingHorizontal: 8 }}>
        {data.entries.map(([idx, val]) => {
          const ratio = val / data.maxVal;
          const barH = Math.max(8, Math.round(ratio * (height - 50))); // leave space for labels
          const label = options?.[idx]?.label ?? `Option ${idx + 1}`;
          const emoji = options?.[idx]?.emoji ?? '';
          return (
            <View key={idx} style={{ flex: 1, alignItems: 'center', marginHorizontal: 6 }}>
              <View style={{ justifyContent: 'flex-end', height: height - 40, width: '100%', position: 'relative' }}>
                {/* Count above bar */}
                <View style={{ position: 'absolute', bottom: barH + 6, left: 0, right: 0, alignItems: 'center' }}>
                  <Text style={{ fontSize: 12, color: '#333' }}>{val}</Text>
                </View>
                <View style={{ height: barH, backgroundColor: barColor, borderRadius: 8 }} />
              </View>
              <Text style={{ marginTop: 6, fontSize: 12, color: '#333' }} numberOfLines={1}>
                {emoji}
              </Text>
              <Text style={{ fontSize: 11, color: '#666' }} numberOfLines={1}>
                {label}
              </Text>
            </View>
          );
        })}
        </View>
      </View>
    </View>
  );
}


